import { Injectable } from '@angular/core';
import { AvailableLangs, TranslocoService } from '@ngneat/transloco';
import { ActivatedRoute, NavigationExtras, Params, Router } from '@angular/router';
import { LocalStorageService } from '@cdk/browser';

const key = 'lang';

@Injectable({ providedIn: 'root' })
export class LocalizeService {
  private storageKey: string = key;

  locales: AvailableLangs = this.transloco.getAvailableLangs();
  defaultLang = this.transloco.getDefaultLang();
  currentLang: string;

  constructor(
    private readonly transloco: TranslocoService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly storage: LocalStorageService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {
    this.init();
    this.currentLang = this.transloco.getActiveLang() || this.defaultLang;
  }

  /**
   * Сменить язык и перейти к переведенному маршруту
   */
  changeLanguage(language: string, extras?: NavigationExtras): void {
    this.transloco.setActiveLang(language);
    this.save(language);

    if (language !== this.currentLang) {
      this.currentLang = language;

      const url = this.router.url;
      const extrasToApply: NavigationExtras = extras ? { ...extras } : {};
      const queryParamsObj: Params = this.chooseQueryParams(extras, this.route.snapshot.queryParams);

      if (queryParamsObj) {
        // eslint-disable-next-line functional/immutable-data
        extrasToApply.queryParams = queryParamsObj;
      }
      this.navigate(url, extrasToApply);
    }
  }

  isCurrentLangRu(): boolean {
    return this.currentLang === 'ru';
  }

  private navigate(url: string, extras: NavigationExtras): void {
    const oldShouldReuseRoute = this.router.routeReuseStrategy.shouldReuseRoute;
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    this.router
      .navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: extras.queryParams,
        queryParamsHandling: 'merge',
      })
      .then(() => {
        this.router.routeReuseStrategy.shouldReuseRoute = oldShouldReuseRoute;
        this.router.onSameUrlNavigation = 'ignore';
      });
  }

  private init(): void {
    // Нам нужно сначала установить кешированный язык, а затем прослушивать изменения
    this.setActiveLang();
  }

  /**
   * Стратегия выбора между новым или старым queryParams
   * @param newExtras extras that containes new QueryParams
   * @param currentQueryParams current query params
   */
  private chooseQueryParams(newExtras: NavigationExtras | undefined, currentQueryParams: Params): Params {
    let queryParamsObj: Params = {};
    if (newExtras && newExtras.queryParams) {
      queryParamsObj = newExtras.queryParams;
    } else if (currentQueryParams) {
      queryParamsObj = currentQueryParams;
    }
    return queryParamsObj;
  }

  private setActiveLang(): void {
    const cachedLang: string = this.storage.getItem(this.storageKey);
    const defaultLang = this.transloco.config.defaultLang;
    const activeLang = cachedLang || defaultLang;
    if (activeLang) {
      this.storage.setItem(this.storageKey, activeLang);
      this.transloco.setActiveLang(activeLang);
      this.currentLang = activeLang;
    }
  }

  private save(lang: string): void {
    if (this.transloco.config.prodMode === false) {
      console.log(`%c 🍻 Saving ${lang} to storage`, `background: #fff; color: #2196F3;`);
    }
    this.storage.setItem(this.storageKey, lang);
  }
}
